<template>
    <div class="relative h-screen overflow-y-auto bg-gray-200">
        <!--
          `sticky` makes this a stacking context, so dropdowns in the header slots cannot rise above the page
          body on their own: `z-40` lifts the whole header over it instead.
        -->
        <header class="sticky left-0 z-40">
            <div
                v-if="slots.breadcrumb || slots.search"
                class="bg-gray-100 border-b border-b-gray-200 text-sm flex flex-row items-center justify-between px-4 min-h-12"
            >
                <slot name="breadcrumb" />

                <slot name="search" />
            </div>

            <div class="bg-gray-50 relative py-4 pr-4" :class="[props.variant === 'Logo' ? 'pl-36' : 'pl-4']">
                <div
                    v-if="props.variant === 'Logo'"
                    class="bg-gray-50 ring-1 ring-gray-200 size-28 rounded-lg absolute left-4 top-2 p-2 flex items-center justify-center"
                    :class="{ 'border-4 border-red-700 bg-red-200': hasLoadingImageFailed }"
                >
                    <p
                        v-if="hasLoadingImageFailed"
                        class="text-center hyphens-auto p-1 max-w-full max-h-full leading-tight"
                    >
                        {{ $t('loading-logo-failed') }}
                    </p>

                    <Tooltip>
                        <template #trigger="tooltipProps">
                            <img
                                v-if="props.logoUrl && !hasLoadingImageFailed"
                                :src="props.logoUrl"
                                :alt="props.logoAlt"
                                v-bind="tooltipProps"
                                @error="onImageError"
                            />

                            <p
                                v-if="!props.logoUrl"
                                class="text-ellipsis hyphens-auto overflow-hidden max-h-28 max-w-28 px-2 leading-tight"
                            >
                                {{ props.logoAlt }}
                            </p>
                        </template>

                        <template #text>{{ $t('logo-edit-hint') }}</template>
                    </Tooltip>
                </div>
                <div class="flex flex-wrap gap-4 items-center justify-between">
                    <div class="flex gap-4 items-center font-semibold text-5xl">
                        <div v-if="$slots.icon" class="text-gray-600">
                            <slot name="icon" />
                        </div>
                        <h1 v-if="$slots.headline" class="hyphens-auto text-gray-700">
                            <slot name="headline"></slot>
                        </h1>
                        <slot name="headline-extra" />
                    </div>

                    <div class="flex flex-wrap gap-2">
                        <slot name="buttons" />
                    </div>
                </div>
            </div>

            <div class="bg-gray-100 border-t border-t-gray-200" :class="[props.variant === 'Logo' ? 'pl-36' : 'pl-4']">
                <slot name="tab-navigation" />
            </div>
        </header>

        <main>
            <slot name="body" />
        </main>
    </div>
</template>

<script setup lang="ts">
import { useImageError } from '@/utils/useImageError';
import { useSlots } from 'vue';
import Tooltip from '../Tooltip/Tooltip.vue';

type HeaderCardsLayoutProps = {
    logoUrl?: string;
    logoAlt?: string;
    variant: 'Logo' | 'No-Logo';
};

const props = defineProps<HeaderCardsLayoutProps>();

const slots = useSlots();

defineEmits<{
    (e: 'enterEditMode'): void;
    (e: 'leaveEditMode'): void;
    (e: 'discardChanges'): void;
    (e: 'save'): void;
}>();

const { hasLoadingImageFailed, onImageError } = useImageError(false);
</script>
